/**
 * DUBVM-93: every monetary and percentage figure displays at exactly two
 * decimals, platform-wide — "5.00", never "5", "5.0" or "5.000". This is the
 * single place that decides that, so the backsheet, Overview, quotes and
 * exports cannot drift apart again (they showed 3dp in some places and 1dp or
 * 0dp in others). Display only: stored precision is unchanged.
 *
 * DUBVM-76's rule still holds inside this one: a computed rate of 15.5 must
 * display as 15.50, never rounded to 16, so displayed rate × quantity
 * reconciles with the displayed total.
 */
export const MONEY_DECIMALS = 2;

/** Format a plain number with grouping and exactly `decimals` fraction digits (standard rounding). */
export function formatNumber(value: number, decimals = MONEY_DECIMALS): string {
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    }).format(value);
}

/**
 * Format a numeric amount with currency code prefix.
 * Shows "USD 1,000.00" instead of "$1,000".
 * @param value    The numeric value to format.
 * @param currency ISO 4217 currency code (default: 'USD').
 * @param decimals Number of fraction digits (default: 2, DUBVM-93).
 */
export function formatCurrency(value: number, currency = 'USD', decimals = MONEY_DECIMALS): string {
    return `${currency} ${formatNumber(value, decimals)}`;
}

/** Return the currency code and formatted number as separate parts. */
export function formatCurrencyParts(
    value: number,
    currency = 'USD',
    decimals = MONEY_DECIMALS,
): { code: string; amount: string } {
    return { code: currency, amount: formatNumber(value, decimals) };
}

/**
 * Format a percentage value (already ×100, e.g. 22.03 for 22.03%) with exactly
 * two decimals: "22.03%", "15.00%", "-3.43%".
 */
export function formatPercent(value: number, decimals = MONEY_DECIMALS): string {
    return `${formatNumber(value, decimals)}%`;
}

/** Uppercase the first character of a string. */
export function capitalize(str: string): string {
    if (!str) {
        return str;
    }
    return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Format an ISO timestamp as a readable date string.
 * Example: "09 Apr 2026"
 */
export function formatDate(iso: string): string {
    const d = new Date(iso);
    if (isNaN(d.getTime())) {
        return iso;
    }
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

/**
 * Format an ISO timestamp as a readable date-time string.
 * Example: "09 Apr 2026, 2:35 PM"
 */
export function formatDateTime(iso: string): string {
    const d = new Date(iso);
    if (isNaN(d.getTime())) {
        return iso;
    }
    return formatDate(iso) + ', ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

/** Human-readable relative time string (e.g. "5m ago", "2h ago", "3d ago"). */
export function timeAgo(iso: string): string {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) {
        return 'just now';
    }
    if (mins < 60) {
        return `${mins}m ago`;
    }
    const hours = Math.floor(mins / 60);
    if (hours < 24) {
        return `${hours}h ago`;
    }
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
}

/** Extract up to 2 uppercase initials from a full name (e.g. "John Doe" → "JD"). */
export function initials(name: string): string {
    return name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
}
